/**
 * Session storage for scheduler UI.
 * Production: Portal2 sets env-scoped `jwtAuth_{env}` and `projectsInfo_{env}` cookies
 * (see portalSession.ts / ssoCookieNames.ts).
 */

import {
  applyPortalSessionFromCookies,
  getPortalSessionSnapshot,
  type PortalSessionSnapshot,
} from './portalSession';

export {
  applyPortalSessionFromCookies,
  getPortalSessionSnapshot,
  getPortalReturnUrl,
  getPortalUserId,
  getPortalUserName,
  getPortalProjectList,
  getPortalProjectImage,
  switchPortalProject,
  syncSharedSessionCookies,
  parseProjectsInfoCookie,
  PORTAL_COOKIE_JWT,
  PORTAL_COOKIE_PROJECTS_INFO,
  type PortalProjectsInfo,
  type PortalSessionSnapshot,
  type PortalProjectSwitchResult,
  isPortalDevReturnUrl,
} from './portalSession';

export { clearPersistedPortalSession as clearStoredAuth } from './portalSession';
export type { PortalProjectOption } from './portalProjectList';

import { getCookie } from './cookieIO';

export { getCookie, setCookie, deleteCookie, setPortalCompatibleCookie } from './cookieIO';
export {
  performLogout,
  performLogoutSync,
  redirectToPortal,
  getLogoutReturnUrl,
  LOGOUT_PATH,
  isLogoutPath,
} from './logout';
export { peekPortalReturnUrl } from './portalSession';
export {
  buildPortalAppUrl,
  openPortalUrl,
  type BuildPortalAppUrlOptions,
  type OpenPortalUrlOptions,
} from './portalNavigation';

function legacyAuthToken(): string {
  return getCookie('authToken') || getCookie('auth_token') || '';
}

function legacyProjectId(): string {
  return getCookie('projectId') || getCookie('project_id') || '';
}

/**
 * Customer scope for request-tracking and other tenant APIs.
 */
export function getCustomerId(): string {
  const fromSession = getPortalSessionSnapshot()?.customerId?.trim();
  if (fromSession) {
    return fromSession;
  }
  const fromEnv = (import.meta.env.VITE_CUSTOMER_ID as string | undefined)?.trim();
  if (fromEnv) {
    return fromEnv;
  }
  return (import.meta.env.VITE_HARDCODED_CUSTOMER_ID as string | undefined)?.trim() || 'myhome';
}

/** Persist customer id after portal handoff (normally set via applyPortalSessionFromCookies). */
export function setCustomerId(_customerId: string): void {
  void _customerId;
}

/**
 * Active planning project id (`projectsInfo.selectedProj.id`).
 */
export function getProjectId(): string {
  const fromSession = getPortalSessionSnapshot()?.projectId?.trim();
  if (fromSession) {
    return fromSession;
  }
  const legacy = legacyProjectId();
  if (legacy) {
    return legacy;
  }
  return (import.meta.env.VITE_PROJECT_ID as string | undefined)?.trim() ?? '';
}

/**
 * JWT from portal env-scoped SSO cookie / session snapshot.
 */
export function getAuthToken(): string {
  const fromSession = getPortalSessionSnapshot()?.authToken?.trim();
  if (fromSession) {
    return fromSession;
  }
  const legacy = legacyAuthToken();
  if (legacy) {
    return legacy;
  }
  return (import.meta.env.VITE_AUTH_TOKEN as string | undefined)?.trim() ?? '';
}

/** @deprecated Use portal session; kept for dev settings UI only. */
export function setProjectId(projectId: string): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    if (projectId) {
      localStorage.setItem('gantt-planner:projectId', projectId);
    } else {
      localStorage.removeItem('gantt-planner:projectId');
    }
  } catch {
    // ignore
  }
}

/** @deprecated Use portal session; kept for dev settings UI only. */
export function setAuthToken(authToken: string): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    if (authToken) {
      localStorage.setItem('gantt-planner:jwtAuth', authToken);
    } else {
      localStorage.removeItem('gantt-planner:jwtAuth');
    }
  } catch {
    // ignore
  }
}
